/**
 * HelpPanel — contextual slide-out help drawer.
 *
 * Renders route-aware help content. The panel slides in from the right
 * edge and can be dismissed with Escape or the close button.
 *
 * Cross-References:
 *   - FR-UX-03: Contextual help
 *   - UI Screen Spec: edge-cases-error-handling.md
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { HelpCircle, X, ExternalLink, ChevronRight } from 'lucide-react';

/* ── Help content registry ─────────────────────────────────── */

interface HelpSection {
  title: string;
  body: string;
  links?: Array<{ label: string; href: string }>;
}

interface HelpTopic {
  heading: string;
  sections: HelpSection[];
}

/**
 * Route-keyed help content. Keys are matched against `location.pathname`
 * using startsWith so `/admin/users/123` matches `/admin/users`.
 */
const HELP_CONTENT: Record<string, HelpTopic> = {
  '/dashboard': {
    heading: 'Dashboard',
    sections: [
      {
        title: 'Widgets',
        body: 'Your dashboard is composed of widgets — small panels that display key information at a glance. Widgets update automatically based on their configured refresh interval.',
      },
      {
        title: 'Customisation',
        body: 'Drag widgets to rearrange them or use the "Add Widget" button to bring in new ones. Click the gear icon on any widget to configure its settings.',
      },
    ],
  },
  '/admin/users': {
    heading: 'User Management',
    sections: [
      {
        title: 'Creating Users',
        body: 'Click "Invite User" to send an email invitation. The user will complete onboarding when they first log in via their identity provider.',
      },
      {
        title: 'Roles & Permissions',
        body: 'Assign roles to control what each user can access. System roles cannot be edited but custom roles can be tailored to your needs.',
      },
      {
        title: 'Deactivation',
        body: 'Deactivating a user immediately revokes all active sessions and prevents future logins. User data is preserved for audit purposes.',
      },
    ],
  },
  '/admin/roles': {
    heading: 'Role Management',
    sections: [
      {
        title: 'System Roles',
        body: 'System roles (Admin, Member, Viewer) are pre-defined and cannot be modified or deleted. They provide baseline access patterns.',
      },
      {
        title: 'Custom Roles',
        body: 'Create custom roles to match your organisation\'s structure. Assign fine-grained permissions by resource type (users, roles, integrations, etc.).',
      },
      {
        title: 'Default Role',
        body: 'Mark one role as "default" — it will be automatically assigned to new users. You can change the default in Tenant Settings.',
      },
    ],
  },
  '/admin/settings': {
    heading: 'Tenant Settings',
    sections: [
      {
        title: 'General',
        body: 'Configure your organisation name, timezone, and default role. The tenant slug is set at provisioning and cannot be changed.',
      },
      {
        title: 'Branding',
        body: 'Upload your logo and set a primary colour to customise the platform appearance. Changes are visible immediately to all users.',
      },
      {
        title: 'Identity Providers',
        body: 'Add OIDC-compatible identity providers (Google Workspace, Microsoft Entra, Okta, or custom). At least one active provider is required.',
      },
      {
        title: 'Security',
        body: 'Control session timeout, concurrent session limits, and whether sensitive actions (like changing roles) require re-authentication.',
      },
    ],
  },
  '/admin/audit': {
    heading: 'Audit Log',
    sections: [
      {
        title: 'Browsing Logs',
        body: 'The audit log records every significant action in your tenant. Click a row to expand its details, including the full change payload and correlation ID.',
      },
      {
        title: 'Filtering',
        body: 'Use the search bar and filter panel to narrow results by actor, action type, resource, or date range. Filters apply to exports too.',
      },
      {
        title: 'Exporting',
        body: 'Export logs in CSV (for spreadsheets) or JSON (for programmatic analysis). Exports respect the current filter selections.',
        links: [
          { label: 'SOC 2 Audit Requirements', href: 'https://docs.vortexintra.com/compliance/soc2' },
        ],
      },
    ],
  },
  '/integrations': {
    heading: 'Integrations',
    sections: [
      {
        title: 'Your connections',
        body: 'OAuth connections you own — Connected Workspace providers (Atlassian, Box, Google Drive, HubSpot, Microsoft 365) and Atlassian for IT Service Desk. Each module declares what it needs; the page surfaces "Connect" prompts for unmet dependencies.',
      },
      {
        title: 'Tenant Privileged Connections (admin only)',
        body: 'Tenant-owned service-account credentials used by modules for privileged operations — ELC user provisioning, ELC HRIS source (Rippling), ITSD service-health monitoring (UptimeRobot, Microsoft 365), and the unified inbound/outbound webhook + AWS + SIEM shapes. Configure once; every user benefits.',
      },
    ],
  },
  '/admin/modules': {
    heading: 'Modules',
    sections: [
      {
        title: 'Module Management',
        body: 'Enable or disable platform modules to tailor the feature set for your organisation. Disabled modules hide their navigation items and API endpoints.',
      },
    ],
  },
};

/* ── Component ──────────────────────────────────────────────── */

interface HelpPanelProps {
  /** Current route path (e.g. from useLocation().pathname). */
  currentPath: string;
}

export default function HelpPanel({ currentPath }: HelpPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /* Find best-match help topic for current route */
  const topic = Object.entries(HELP_CONTENT)
    .filter(([prefix]) => currentPath.startsWith(prefix))
    .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ?? null;

  /* Keyboard: Escape closes, trap focus inside panel when open */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /* Close when route changes */
  useEffect(() => {
    setIsOpen(false);
  }, [currentPath]);

  return (
    <>
      {/* Trigger button — can be placed in the shell header */}
      <button
        ref={triggerRef}
        className="help-panel__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle help panel"
        aria-expanded={isOpen}
        title="Help"
      >
        <HelpCircle size={20} />
      </button>

      {/* Backdrop */}
      {isOpen && <div className="help-panel__backdrop" onClick={() => setIsOpen(false)} />}

      {/* Panel */}
      <aside
        ref={panelRef}
        className={`help-panel ${isOpen ? 'help-panel--open' : ''}`}
        aria-label="Help panel"
        role="complementary"
      >
        <div className="help-panel__header">
          <h2 className="help-panel__title">
            <HelpCircle size={18} />
            {topic ? topic.heading : 'Help'}
          </h2>
          <button
            className="help-panel__close"
            onClick={() => { setIsOpen(false); triggerRef.current?.focus(); }}
            aria-label="Close help panel"
          >
            <X size={18} />
          </button>
        </div>

        <div className="help-panel__body">
          {topic ? (
            topic.sections.map((section, i) => (
              <div key={i} className="help-panel__section">
                <h3 className="help-panel__section-title">
                  <ChevronRight size={14} /> {section.title}
                </h3>
                <p className="help-panel__section-body">{section.body}</p>
                {section.links && section.links.length > 0 && (
                  <ul className="help-panel__links">
                    {section.links.map((link, j) => (
                      <li key={j}>
                        <a href={link.href} target="_blank" rel="noopener noreferrer" className="help-panel__link">
                          {link.label} <ExternalLink size={12} />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          ) : (
            <div className="help-panel__no-content">
              <p>No help content available for this page.</p>
              <p className="help-panel__no-content-hint">
                Navigate to a specific page to see contextual help.
              </p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
